import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import { api, API_BASE_URL, getAccessToken, refreshSession } from '../lib/api';
import { SignInPage } from './SignInPage';

/**
 * Private, full-screen presentation pages (e.g. /talks/aic-genai).
 *
 * The deck is NOT in the public studio bundle. box-api serves it (services/box-api/src/routes/talks.ts) only to the
 * emails in its TALK_VIEWERS list: this page asks for a 12-hour ticket and frames
 * `<box-api>/box/talks/<slug>/t/<ticket>/index.html`. Anyone else signed in sees a "private" notice.
 *
 * Bridge to the deck (postMessage across origins: the deck runs on box-api's origin and is told ours via ?host=):
 * the deck asks for `live-init` and receives the API base, a fresh access token for its tally socket and the
 * audience QR code. Phase changes (`live-control`) and "start a fresh session" (`live-archive`) are made here with
 * the studio's own authenticated client, so token refresh keeps working through a long talk.
 */
export function TalkPage({ slug, title, liveCode }: { slug: string; title: string; liveCode?: string }) {
  const { status } = useAuth();
  const frame = useRef<HTMLIFrameElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [denied, setDenied] = useState<string | null>(null);
  const deckOrigin = new URL(API_BASE_URL).origin;

  useEffect(() => {
    document.title = title;
  }, [title]);

  // Ask box-api for a ticket; only presenters get one.
  useEffect(() => {
    if (status !== 'authenticated') return;
    let gone = false;
    api.post<{ ticket: string }>(`/box/talks/${slug}/ticket`, {})
      .then(r => { if (!gone) setSrc(`${API_BASE_URL}/box/talks/${slug}/t/${r.ticket}/index.html?host=${encodeURIComponent(window.location.origin)}${window.location.hash}`); })
      .catch((e: unknown) => { if (!gone) setDenied((e as { status?: number })?.status === 404 ? 'This presentation is not available.' : 'This presentation is private.'); });
    return () => { gone = true; };
  }, [slug, status]);

  useEffect(() => {
    if (status !== 'authenticated' || !liveCode || !src) return;
    const audienceUrl = `${window.location.origin}/live/${liveCode}`;
    const onMessage = async (ev: MessageEvent) => {
      if (ev.origin !== deckOrigin || ev.source !== frame.current?.contentWindow) return;
      const msg = ev.data || {};
      const reply = (data: unknown) => frame.current?.contentWindow?.postMessage(data, deckOrigin);
      if (msg.type === 'live-init' || msg.type === 'live-token') {
        await refreshSession().catch(() => false);
        const qr = await QRCode.toDataURL(audienceUrl, { margin: 1, width: 600, color: { dark: '#1B2431', light: '#ffffff' } });
        reply({ type: 'live-init', code: liveCode, api: API_BASE_URL, token: getAccessToken(), audienceUrl, qr });
      } else if (msg.type === 'live-archive') {
        // "Start a fresh session" on the title slide: archive this run's answers and reopen the lobby.
        api.post<{ archived: { responses: number; votes: number; signups: number }; run: number }>(`/box/live-host/${liveCode}/archive`, {})
          .then(r => reply({ type: 'live-archived', ok: true, ...r }))
          .catch(() => reply({ type: 'live-archived', ok: false }));
      } else if (msg.type === 'live-control') {
        api.post(`/box/live-host/${liveCode}/control`, { phase: msg.phase, round: msg.round, revealed: msg.revealed }).catch(() => undefined);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [deckOrigin, liveCode, src, status]);

  if (status === 'loading' || (status === 'authenticated' && !src && !denied)) {
    return (
      <div className="grid h-full place-items-center">
        <div className="skeleton h-9 w-9 rounded-2xl" />
      </div>
    );
  }
  if (status === 'anonymous') return <SignInPage />;
  if (denied) {
    return (
      <div className="grid h-full place-items-center p-6 text-center">
        <div className="max-w-sm space-y-2">
          <h1 className="text-xl font-semibold">{denied}</h1>
          <p className="text-sm text-muted">Only the presenter's account can open it. If that's you, sign in with that account.</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      ref={frame}
      title={title}
      src={src!}
      className="fixed inset-0 h-full w-full border-0"
      allow="fullscreen; autoplay; clipboard-write"
      allowFullScreen
      // Keyboard navigation goes to the deck straight away.
      onLoad={() => frame.current?.contentWindow?.focus()}
    />
  );
}
